import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  Select,
  Button,
  FormControl,
  FormLabel,
  InputGroup,
  InputLeftElement,
  Box,
  Badge,
  Spinner,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react'
import { MagnifyingGlass, ShoppingBag, ChatTeardropDots, Paperclip } from '@phosphor-icons/react'
import { useDropzone } from 'react-dropzone'
import api from '../../services/api'
import { useFileUpload } from '../../hooks/useFileUpload'
import { UploadProgressList } from '../../components/UploadProgress'
import {
  TICKET_DROPZONE_ACCEPT,
  TICKET_FILE_INPUT_ACCEPT,
  MAX_TICKET_ATTACHMENTS,
  filesFromClipboard,
  toAttachmentPayload,
  PendingFileChips,
} from './attachments'

interface Agent {
  id: number
  name: string
}

interface OrderResult {
  id: number
  channelOrderNumber: string
  status: string
  soldTotal: number
  createdAt: string
  customerName: string
}

interface ChatResult {
  id: number
  protocol: string | null
  status: string
  createdAt: string
  ownerName: string
  categoryName: string | null
  categoryColor: string | null
}

interface TicketCategory {
  id: number
  name: string
  color: string
  description: string | null
}

export default function CreateTicketModal({
  isOpen,
  onClose,
  onCreated,
  prefilledChatId,
}: {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  prefilledChatId?: number
}) {
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [assignedToAgentId, setAssignedToAgentId] = useState('')
  const [priority, setPriority] = useState('medium')
  const [categoryId, setCategoryId] = useState('')
  const [agents, setAgents] = useState<Agent[]>([])
  const [categories, setCategories] = useState<TicketCategory[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [orderSearch, setOrderSearch] = useState('')
  const [orderResults, setOrderResults] = useState<OrderResult[]>([])
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null)
  const [selectedOrderLabel, setSelectedOrderLabel] = useState('')
  const [isSearchingOrders, setIsSearchingOrders] = useState(false)

  const [chatSearch, setChatSearch] = useState('')
  const [chatResults, setChatResults] = useState<ChatResult[]>([])
  const [selectedChatId, setSelectedChatId] = useState<number | null>(prefilledChatId || null)
  const [selectedChatLabel, setSelectedChatLabel] = useState(prefilledChatId ? `#${prefilledChatId}` : '')
  const [isSearchingChats, setIsSearchingChats] = useState(false)

  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadFileDetailed, uploads, cancelUpload, clearUpload, clearAllUploads } = useFileUpload()

  const toast = useToast()

  const addPendingFiles = useCallback((files: File[]) => {
    if (files.length === 0) return
    setPendingFiles((prev) => {
      const next = [...prev, ...files]
      if (next.length > MAX_TICKET_ATTACHMENTS) {
        toast({ title: `Máximo de ${MAX_TICKET_ATTACHMENTS} arquivos por ticket`, status: 'warning', duration: 3000 })
        return next.slice(0, MAX_TICKET_ATTACHMENTS)
      }
      return next
    })
  }, [toast])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: addPendingFiles,
    accept: TICKET_DROPZONE_ACCEPT,
    multiple: true,
    noClick: true,
    noKeyboard: true,
  })

  const surfaceBg = useColorModeValue('white', 'gray.800')
  const subtleBg = useColorModeValue('gray.100', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const borderSubtle = useColorModeValue('gray.300', 'gray.600')
  const hoverBg = useColorModeValue('gray.100', 'gray.600')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textSecondary = useColorModeValue('gray.600', 'gray.300')
  const textMuted = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    if (isOpen) {
      fetchAgents()
      fetchCategories()
      if (prefilledChatId) {
        setSelectedChatId(prefilledChatId)
        setSelectedChatLabel(`Atendimento #${prefilledChatId}`)
      }
    }
  }, [isOpen, prefilledChatId])

  const fetchAgents = async () => {
    try {
      const response = await api.get('/agents')
      setAgents(response.data.data || response.data)
    } catch (error) {
      console.error('Error fetching agents:', error)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await api.get('/internal-ticket-categories')
      setCategories(response.data || [])
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const searchOrders = useCallback(async (search: string) => {
    if (search.length < 1) {
      setOrderResults([])
      return
    }
    setIsSearchingOrders(true)
    try {
      const params: any = { search }
      if (selectedChatId) {
        params.supportChatId = selectedChatId
      }
      const response = await api.get('/internal-tickets/search-orders', { params })
      setOrderResults(response.data.data)
    } catch (error) {
      console.error('Error searching orders:', error)
    } finally {
      setIsSearchingOrders(false)
    }
  }, [selectedChatId])

  const searchChats = useCallback(async (search: string) => {
    if (search.length < 2) {
      setChatResults([])
      return
    }
    setIsSearchingChats(true)
    try {
      const response = await api.get('/internal-tickets/search-chats', { params: { search } })
      setChatResults(response.data.data)
    } catch (error) {
      console.error('Error searching chats:', error)
    } finally {
      setIsSearchingChats(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => searchOrders(orderSearch), 400)
    return () => clearTimeout(timer)
  }, [orderSearch, searchOrders])

  useEffect(() => {
    const timer = setTimeout(() => searchChats(chatSearch), 400)
    return () => clearTimeout(timer)
  }, [chatSearch, searchChats])

  const handleSelectOrder = (order: OrderResult) => {
    setSelectedOrderId(order.id)
    setSelectedOrderLabel(`#${order.id} (${order.channelOrderNumber}) - ${order.customerName}`)
    setOrderSearch('')
    setOrderResults([])
  }

  const handleSelectChat = (chat: ChatResult) => {
    setSelectedChatId(chat.id)
    setSelectedChatLabel(`${chat.protocol || `#${chat.id}`} - ${chat.ownerName}`)
    setChatSearch('')
    setChatResults([])
  }

  const resetForm = () => {
    setSubject('')
    setMessage('')
    setAssignedToAgentId('')
    setPriority('medium')
    setCategoryId('')
    setSelectedOrderId(null)
    setSelectedOrderLabel('')
    setSelectedChatId(prefilledChatId || null)
    setSelectedChatLabel(prefilledChatId ? `Atendimento #${prefilledChatId}` : '')
    setOrderSearch('')
    setChatSearch('')
    setPendingFiles([])
    clearAllUploads()
  }

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim() || !assignedToAgentId) {
      toast({ title: 'Preencha todos os campos obrigatórios', status: 'warning', duration: 3000 })
      return
    }
    setIsSubmitting(true)
    let attachments: ReturnType<typeof toAttachmentPayload>[] = []
    try {
      const uploaded = await Promise.all(pendingFiles.map((file) => uploadFileDetailed(file)))
      attachments = uploaded.map(toAttachmentPayload)
    } catch {
      toast({ title: 'Erro ao enviar arquivo', description: 'O ticket não foi criado; tente de novo.', status: 'error', duration: 5000 })
      setIsSubmitting(false)
      return
    }
    try {
      await api.post('/internal-tickets', {
        subject: subject.trim(),
        message: message.trim(),
        assignedToAgentId: Number(assignedToAgentId),
        supportChatId: selectedChatId,
        orderId: selectedOrderId,
        priority,
        categoryId: categoryId ? Number(categoryId) : null,
        attachments,
      })
      toast({ title: 'Ticket criado com sucesso', status: 'success', duration: 3000 })
      resetForm()
      onCreated()
    } catch (error: any) {
      toast({
        title: 'Erro ao criar ticket',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg={surfaceBg} maxH="90vh">
        <ModalHeader borderBottom="1px" borderColor={borderColor}>
          <Text color={textPrimary}>Novo Ticket Interno</Text>
        </ModalHeader>
        <ModalCloseButton color={textMuted} />
        <ModalBody py={4}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textSecondary}>Assunto</FormLabel>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Descreva o assunto do ticket"
                bg={subtleBg}
                borderColor={borderSubtle}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textSecondary}>Atendente Destinatário</FormLabel>
              <Select
                value={assignedToAgentId}
                onChange={(e) => setAssignedToAgentId(e.target.value)}
                placeholder="Selecione um atendente"
                bg={subtleBg}
                borderColor={borderSubtle}
              >
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id} style={{ background: '#2D3748' }}>
                    {agent.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Prioridade</FormLabel>
              <Select
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                bg={subtleBg}
                borderColor={borderSubtle}
              >
                <option value="low" style={{ background: '#2D3748' }}>Baixa</option>
                <option value="medium" style={{ background: '#2D3748' }}>Média</option>
                <option value="high" style={{ background: '#2D3748' }}>Alta</option>
                <option value="urgent" style={{ background: '#2D3748' }}>Urgente</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>Categoria</FormLabel>
              <Select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                placeholder="Selecione uma categoria (opcional)"
                bg={subtleBg}
                borderColor={borderSubtle}
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id} style={{ background: '#2D3748' }}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* Vincular Atendimento */}
            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                <HStack spacing={1}>
                  <ChatTeardropDots size={14} />
                  <Text>Vincular Atendimento (opcional)</Text>
                </HStack>
              </FormLabel>
              {selectedChatId && !prefilledChatId ? (
                <HStack bg={subtleBg} p={2} borderRadius="md" justify="space-between">
                  <Text fontSize="sm" color={textPrimary} noOfLines={1}>{selectedChatLabel}</Text>
                  <Button size="xs" variant="ghost" color="red.400" onClick={() => { setSelectedChatId(null); setSelectedChatLabel('') }}>
                    Remover
                  </Button>
                </HStack>
              ) : prefilledChatId ? (
                <HStack bg={subtleBg} p={2} borderRadius="md">
                  <ChatTeardropDots size={14} color="#63B3ED" />
                  <Text fontSize="sm" color={textPrimary}>{selectedChatLabel}</Text>
                </HStack>
              ) : (
                <Box position="relative">
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <MagnifyingGlass size={14} color="gray" />
                    </InputLeftElement>
                    <Input
                      value={chatSearch}
                      onChange={(e) => setChatSearch(e.target.value)}
                      placeholder="Buscar por protocolo, ID ou nome..."
                      bg={subtleBg}
                      borderColor={borderSubtle}
                    />
                  </InputGroup>
                  {(chatResults.length > 0 || isSearchingChats) && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg={subtleBg}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderSubtle}
                      maxH="150px"
                      overflowY="auto"
                      zIndex={10}
                      mt={1}
                    >
                      {isSearchingChats ? (
                        <Spinner size="sm" color="yellow.500" m={3} />
                      ) : (
                        chatResults.map((chat) => (
                          <HStack
                            key={chat.id}
                            p={2}
                            cursor="pointer"
                            _hover={{ bg: hoverBg }}
                            onClick={() => handleSelectChat(chat)}
                          >
                            <Text fontSize="xs" color="yellow.400" fontWeight="bold">
                              {chat.protocol || `#${chat.id}`}
                            </Text>
                            <Text fontSize="xs" color={textPrimary} noOfLines={1}>{chat.ownerName}</Text>
                            {chat.categoryName && (
                              <Badge fontSize="2xs" bg={chat.categoryColor || 'blue.500'} color="white">
                                {chat.categoryName}
                              </Badge>
                            )}
                          </HStack>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>

            {/* Vincular Pedido */}
            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                <HStack spacing={1}>
                  <ShoppingBag size={14} />
                  <Text>Vincular Pedido (opcional)</Text>
                </HStack>
              </FormLabel>
              {selectedOrderId ? (
                <HStack bg={subtleBg} p={2} borderRadius="md" justify="space-between">
                  <Text fontSize="sm" color={textPrimary} noOfLines={1}>{selectedOrderLabel}</Text>
                  <Button size="xs" variant="ghost" color="red.400" onClick={() => { setSelectedOrderId(null); setSelectedOrderLabel('') }}>
                    Remover
                  </Button>
                </HStack>
              ) : (
                <Box position="relative">
                  <InputGroup size="sm">
                    <InputLeftElement>
                      <MagnifyingGlass size={14} color="gray" />
                    </InputLeftElement>
                    <Input
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Buscar por ID do pedido..."
                      bg={subtleBg}
                      borderColor={borderSubtle}
                    />
                  </InputGroup>
                  {(orderResults.length > 0 || isSearchingOrders) && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg={subtleBg}
                      borderRadius="md"
                      border="1px solid"
                      borderColor={borderSubtle}
                      maxH="150px"
                      overflowY="auto"
                      zIndex={10}
                      mt={1}
                    >
                      {isSearchingOrders ? (
                        <Spinner size="sm" color="yellow.500" m={3} />
                      ) : (
                        orderResults.map((order) => (
                          <HStack
                            key={order.id}
                            p={2}
                            cursor="pointer"
                            _hover={{ bg: hoverBg }}
                            onClick={() => handleSelectOrder(order)}
                          >
                            <Text fontSize="xs" color="yellow.400" fontWeight="bold" flexShrink={0}>
                              #{order.id}
                            </Text>
                            <Text fontSize="xs" color={textMuted} flexShrink={0}>
                              {order.channelOrderNumber}
                            </Text>
                            <Text fontSize="xs" color={textPrimary} noOfLines={1} flex={1}>{order.customerName}</Text>
                            <Badge fontSize="2xs" colorScheme="gray" flexShrink={0}>{order.status}</Badge>
                          </HStack>
                        ))
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" color={textSecondary}>Mensagem</FormLabel>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onPaste={(e) => {
                  const files = filesFromClipboard(e)
                  if (files.length > 0) {
                    e.preventDefault()
                    addPendingFiles(files)
                  }
                }}
                placeholder="Descreva detalhadamente o que precisa..."
                bg={subtleBg}
                borderColor={borderSubtle}
                rows={4}
              />
            </FormControl>

            {/* Anexos */}
            <FormControl>
              <FormLabel fontSize="sm" color={textSecondary}>
                <HStack spacing={1}>
                  <Paperclip size={14} />
                  <Text>Anexos (opcional)</Text>
                </HStack>
              </FormLabel>
              <Box
                {...getRootProps()}
                border="1px dashed"
                borderColor={isDragActive ? 'yellow.400' : borderSubtle}
                bg={isDragActive ? hoverBg : 'transparent'}
                borderRadius="md"
                p={3}
              >
                <input {...getInputProps()} />
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  accept={TICKET_FILE_INPUT_ACCEPT}
                  multiple
                  onChange={(e) => {
                    if (e.target.files) addPendingFiles(Array.from(e.target.files))
                    e.target.value = ''
                  }}
                />
                <VStack spacing={2} align="stretch">
                  <HStack justify="space-between" flexWrap="wrap" gap={2}>
                    <Text fontSize="xs" color={textMuted}>
                      Arraste imagens, vídeos, áudios ou documentos aqui, ou cole um print na mensagem
                    </Text>
                    <Button
                      size="xs"
                      leftIcon={<Paperclip size={14} />}
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      isDisabled={isSubmitting || pendingFiles.length >= MAX_TICKET_ATTACHMENTS}
                    >
                      Escolher arquivos
                    </Button>
                  </HStack>
                  {pendingFiles.length > 0 && (
                    <HStack flexWrap="wrap" gap={2}>
                      <PendingFileChips
                        files={pendingFiles}
                        bg={subtleBg}
                        isDisabled={isSubmitting}
                        onRemove={(index) => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                      />
                    </HStack>
                  )}
                  {uploads.size > 0 && (
                    <UploadProgressList uploads={uploads} onCancel={cancelUpload} onDismiss={clearUpload} />
                  )}
                </VStack>
              </Box>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor={borderColor} gap={3}>
          <Button variant="ghost" onClick={handleClose}>Cancelar</Button>
          <Button
            bg="yellow.500"
            color="gray.900"
            _hover={{ bg: 'yellow.400' }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!subject.trim() || !message.trim() || !assignedToAgentId}
          >
            Criar Ticket
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
